import { DialogRef } from '@angular/cdk/dialog';
import { Component, effect, inject, Signal, viewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EmpoweredProductLicensesStore, EmpoweredProductLicensesStoreType } from '@appState';
import { DIALOG_NO } from '@models/constants';
import {
  CuiButtonComponent,
  CuiButtonCtaComponent,
  CuiCardModule,
  CuiFormComponent,
  CuiInputComponent,
  CuiPreventDialogCloseDirective,
  CuiValidators,
} from 'cui-components';

type GenerateLicenseKeyForm = {
  customerTenantId: FormControl<string>;
  secret: FormControl<string>;
  licenseKey: FormControl<string>;
};

@Component({
  imports: [
    CuiCardModule,
    CuiButtonCtaComponent,
    CuiButtonComponent,
    CuiFormComponent,
    CuiInputComponent,
    CuiPreventDialogCloseDirective,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
  ],
  selector: 'hui-generate-license-key-dialog',
  template: `
    <cui-card cuiPreventDialogClose>
      <cui-card-header [showCloseBtn]="true" (close)="onCancelClick()">
        <h3>Generate license key</h3>
      </cui-card-header>
      <cui-card-body>
        @if (form) {
          <cui-form [formGroup]="form" (save)="onGenerateClick()">
            <cui-input [label]="'Customer tenant ID'" formControlName="customerTenantId" [autoFocus]="true"></cui-input>
            <cui-input [label]="'Secret'" formControlName="secret"></cui-input>
            @if (isGenerated) {
              <div class="result-div">
                @if (isLoading()) {
                  <div class="spinner-container">
                    <mat-spinner class="absolute-centered" mode="indeterminate" [diameter]="25"></mat-spinner>
                  </div>
                }
                @if (!isLoading() && generatedLicenseKey()) {
                  <cui-input [label]="'Generated license key'" formControlName="licenseKey" [copyBtn]="true"></cui-input>
                }
              </div>
            }
          </cui-form>
        }
      </cui-card-body>
      <cui-card-footer>
        <cui-button (clicked)="onCancelClick()">{{ DIALOG_NO }}</cui-button>
        <cui-button-cta [disabled]="(form && (form.disabled || !form.valid || !form.dirty)) || isLoading()" (clicked)="cuiForm()?.submit()">
          Generate</cui-button-cta
        >
      </cui-card-footer>
    </cui-card>
  `,
  styles: `
    .result-div {
      position: relative;
      height: 2.75rem;
      .spinner-container .absolute-centered {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  `,
})
export class GenerateLicenseKeyDialogComponent {
  private empoweredProductLicensesStore: EmpoweredProductLicensesStoreType = inject(EmpoweredProductLicensesStore);
  private dialogRef: DialogRef = inject(DialogRef);
  private formBuilder: FormBuilder = inject(FormBuilder);

  cuiForm: Signal<CuiFormComponent | undefined> = viewChild(CuiFormComponent);

  isLoading = this.empoweredProductLicensesStore.isGenerateLicenseKeyLoading;
  generatedLicenseKey = this.empoweredProductLicensesStore.generatedLicenseKey;

  form!: FormGroup<GenerateLicenseKeyForm>;
  isGenerated = false;

  readonly DIALOG_NO = DIALOG_NO;

  constructor() {
    this.empoweredProductLicensesStore.resetGenerateLicenseKey();

    this.form = this.formBuilder.group({
      customerTenantId: this.formBuilder.control('', {
        nonNullable: true,
        validators: [CuiValidators.required, CuiValidators.validGuid()],
      }),
      secret: this.formBuilder.control('', {
        nonNullable: true,
      }),
      licenseKey: this.formBuilder.control(
        { value: '', disabled: true },
        {
          nonNullable: true,
        },
      ),
    });

    effect(() => {
      const licenseKey = this.generatedLicenseKey();
      if (licenseKey) {
        this.form.controls.licenseKey.setValue(licenseKey);
        this.form.markAsPristine();
      }
    });
  }

  onGenerateClick(): void {
    if (this.form && this.form.valid) {
      const { customerTenantId, secret } = this.form.getRawValue();
      this.isGenerated = true;
      this.empoweredProductLicensesStore.generateLicenseKey({ request: { customerTenantId, secret: secret || undefined } });
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }
}
